import { createBrowserRouter } from "react-router-dom";
import React, { lazy, Suspense } from "react";
// layouts
import MainLayout from "../components/layout/MainLayout";
// protected routes
import ProtectedRoutes from "../components/protectedRoutes/ProtectedRoutes";
import ThreeDotsLoader from "../components/loader/ThreeDotsLoader";
// pages
import Home from "../pages/home/Home";
import MarketerHome from "../pages/home/MarketerHome";
import BusinessOffers from "../pages/business-offers/BusinessOffers";
import JoinNewBusinessForm from "../pages/public/JoinNewBusinessForm";
import JoinRequestSuccess from "../pages/public/JoinRequestSuccess";
import AllowedTo from "../components/layout/AllowedTo";
import { ROLES_ID } from "../utils/roles";
import UserHas from "../components/layout/UserHas";

// lazy pages
const Login = lazy(() => import("../pages/login/Login"));
// cities
const Cities = lazy(() => import("../pages/cities/Cities"));
const AddCity = lazy(() => import("../pages/cities/AddCity"));
const EditCity = lazy(() => import("../pages/cities/EditCity"));
//users
const Users = lazy(() => import("../pages/users/Users"));
const EditUser = lazy(() => import("../pages/users/EditUser"));
// marketers
const Marketers = lazy(() => import("../pages/marketer/Marketers"));
const AddMarketer = lazy(() => import("../pages/marketer/AddMarketer"));
const EditMarketer = lazy(() => import("../pages/marketer/EditMarketer"));
// statistics
const Statistics = lazy(() => import("../pages/statistics/Statistics"));
// businesses
const Businesses = lazy(() => import("../pages/businesses/Businesses"));
const AddBusiness = lazy(() => import("../pages/businesses/AddBusiness"));
const EditBusiness = lazy(() => import("../pages/businesses/EditBusiness"));
const BusinessTags = lazy(() => import("../pages/businesses/BusinessTags"));
const BusinessesOffers = lazy(
  () => import("../pages/business-offers/BusinessOffers")
);
const AddBusinessOffer = lazy(
  () => import("../pages/business-offers/AddBusinessOffer")
);
const EditBusinessOffer = lazy(
  () => import("../pages/business-offers/EditBusinessOffer")
);
// join requests
const BusinessJoinRequests = lazy(
  () => import("../pages/business-join-requests/BusinessJoinRequests")
);
// categories
const AddCategory = lazy(() => import("../pages/categories/AddCategory"));
const EditCategory = lazy(() => import("../pages/categories/EditCategory"));
const BusinessCategories = lazy(
  () => import("../pages/categories/BusinessCategories")
);
const BusinessSubCategories = lazy(
  () => import("../pages/categories/BusinessSubCategories")
);
const FaqCategories = lazy(() => import("../pages/categories/FaqCategories"));
const JobSearchCategories = lazy(() => import("../pages/categories/JobSearchCategories"));
const JobEducationLevelCategories = lazy(() => import("../pages/categories/JobEducationLevelCategories"));
const JobExperienceYearsCategories = lazy(() => import("../pages/categories/JobExperienceYearsCategories"));
const JobNationalityCategories = lazy(() => import("../pages/categories/JobNationalityCategories"));
const HeardAboutCategories = lazy(() => import("../pages/categories/HeardAboutCategories"));
const CategoryTags = lazy(() => import("../pages/categories/CategoryTags"));
// notifications
const PushNotification = lazy(
  () => import("../pages/notifications/PushNotification")
);
const NotificationRequests = lazy(
  () => import("../pages/notifications/NotificationRequests")
);
// tags
const Tags = lazy(() => import("../pages/tags/Tags"));
// faq
const Faq = lazy(() => import("../pages/faq/Faq"));
const AddFaq = lazy(() => import("../pages/faq/AddFaq"));
const EditFaq = lazy(() => import("../pages/faq/EditFaq"));
// suggestions
const Suggestions = lazy(() => import("../pages/suggestions/Suggestions"));
// banners
const Banners = lazy(() => import("../pages/banners/Banners"));
const AddBanner = lazy(() => import("../pages/banners/AddBanner"));
const EditBanner = lazy(() => import("../pages/banners/EditBanner"));
// empty search
const EmptySearchLog = lazy(
  () => import("../pages/empty-search/EmptySearchLog")
);
// settings
const Settings = lazy(() => import("../pages/settings/Settings"));
// business forms
const MarketerBusinessForms = lazy(
  () => import("../pages/marketer-business-forms/MarketerBusinessForms")
);
const AddBusinessForm = lazy(
  () => import("../pages/marketer-business-forms/AddBusinessForm")
);
const EditBusinessForm = lazy(
  () => import("../pages/marketer-business-forms/EditBusinessForm")
);
const BusinessFormImages = lazy(
  () => import("../pages/marketer-business-forms/BusinessFormImages")
);
// settlements
const Settlements = lazy(
  () => import("../pages/marketer-settlements/MarketerSettlements")
);
// log permissions
const LogPermissions = lazy(
  () => import("../pages/log-permissions/LogPermissions")
);
// logs
const Logs = lazy(() => import("../pages/logs/Logs"));
// jobs
const Jobs = lazy(() => import("../pages/jobs/JobsPosting"));
const AddJob = lazy(() => import("../pages/jobs/AddJob"));
const EditJob = lazy(() => import("../pages/jobs/EditJob"));
const ViewJob = lazy(() => import("../pages/jobs/ViewJob"));
const JobApplicants = lazy(() => import("../pages/jobs/JobApplicants"));
const JobTags = lazy(() => import("../pages/jobs/JobTags"));
// job search requests
const JobSearchRequests = lazy(() => import("../pages/job-search-requests/JobSearchRequestsPosting"));
const EditJobSearchRequest = lazy(() => import("../pages/job-search-requests/EditJobSearchRequest"));
const JobSearchRequestTags = lazy(() => import("../pages/job-search-requests/JobSearchRequestTags"));
// service packages
const ServicePackages = lazy(
  () => import("../pages/service-packages/ServicePackages")
);
const AddServicePackage = lazy(
  () => import("../pages/service-packages/AddServicePackage")
);
const EditServicePackage = lazy(
  () => import("../pages/service-packages/EditServicePackage")
);
// service purchase requests
const ServicePurchaseRequests = lazy(
  () => import("../pages/service-purchase-requests/ServicePurchaseRequests")
);
// transactions
const Transactions = lazy(() => import("../pages/transactions/Transactions"));

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <ProtectedRoutes>
        <AllowedTo allowedTo={[ROLES_ID.ADMIN]}>
          <MainLayout />
        </AllowedTo>
      </ProtectedRoutes>
    ),
    children: [
      {
        index: true,
        element: <Home />,
        errorElement: <h1>404</h1>,
      },
      // cities
      {
        path: "cities",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <Cities />
              </Suspense>
            ),
          },
          {
            path: "add",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <AddCity />
              </Suspense>
            ),
          },
          {
            path: ":city_id",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <EditCity />
              </Suspense>
            ),
          },
        ],
      },
      // users
      {
        path: "users",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <Users />
              </Suspense>
            ),
          },
          {
            path: ":user_id",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <EditUser />
              </Suspense>
            ),
          },
        ],
      },
      // marketers
      {
        path: "marketers",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <Marketers />
              </Suspense>
            ),
          },
          {
            path: ":marketer_id",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <EditMarketer />
              </Suspense>
            ),
          },
          {
            path: "add",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <AddMarketer />
              </Suspense>
            ),
          },
        ],
      },
      // statisitcs
      {
        path: "statistics",
        element: (
          <Suspense fallback={<ThreeDotsLoader />}>
            <Statistics />
          </Suspense>
        ),
      },
      // businesses
      {
        path: "businesses",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <Businesses />
              </Suspense>
            ),
          },
          {
            path: ":business_id",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <EditBusiness />
              </Suspense>
            ),
          },
          {
            path: "add",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <AddBusiness />
              </Suspense>
            ),
          },
          {
            path: ":business_id/tags",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <BusinessTags />
              </Suspense>
            ),
          },
          {
            path: ":business_id/offers",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <BusinessOffers />
              </Suspense>
            ),
          },
          {
            path: ":business_id/offers/add",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <AddBusinessOffer />
              </Suspense>
            ),
          },
          {
            path: ":business_id/offers/:offer_id",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <EditBusinessOffer />
              </Suspense>
            ),
          },
        ],
      },
      // offers
      {
        path: "offers",
        children: [
          {
            index: true,
            element: <BusinessOffers />,
          },
        ],
      },
      // jobs
      {
        path: "jobs",
        children: [
          {
            index: true, element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <Jobs />
              </Suspense>
            ),
          },
          {
            path: "add", element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <AddJob />
              </Suspense>
            ),
          },
          {
            path: ":job_id", element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <EditJob />
              </Suspense>
            ),
          },
          {
            path: ":job_id/view", element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <ViewJob />
              </Suspense>
            ),
          },
          {
            path: ":job_id/applicants", element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <JobApplicants />
              </Suspense>
            ),
          },
          {
            path: ":job_id/tags", element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <JobTags />
              </Suspense>
            ),
          },
        ],
      },
      // job search requests
      {
        path: "job-search-requests",
        children: [
          {
            index: true, element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <JobSearchRequests />
              </Suspense>
            ),
          },
          {
            path: ":id", element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <EditJobSearchRequest />
              </Suspense>
            ),
          },
          {
            path: ":id/tags", element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <JobSearchRequestTags />
              </Suspense>
            ),
          },
        ],
      },
      // businessess/join-requests
      {
        path: "businesses/join-requests",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <BusinessJoinRequests />
              </Suspense>
            ),
          },
        ],
      },
      // categories
      {
        path: "categories",
        children: [
          {
            path: "business",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <BusinessCategories />
              </Suspense>
            ),
          },
          {
            path: "faq",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <FaqCategories />
              </Suspense>
            ),
          },
          {
            path: "job-search",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <JobSearchCategories />
              </Suspense>
            ),
          },
          {
            path: "job-education-level",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <JobEducationLevelCategories />
              </Suspense>
            ),
          },
          {
            path: "job-experience-years",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <JobExperienceYearsCategories />
              </Suspense>
            ),
          },
          {
            path: "job-nationality",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <JobNationalityCategories />
              </Suspense>
            ),
          },
          {
            path: "heard-about",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <HeardAboutCategories />
              </Suspense>
            ),
          },
          {
            path: "tags",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <CategoryTags />
              </Suspense>
            ),
          },
          {
            path: "sub",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <BusinessSubCategories />
              </Suspense>
            ),
          },
          {
            path: "add",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <AddCategory />
              </Suspense>
            ),
          },
          {
            path: ":category_id",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <EditCategory />
              </Suspense>
            ),
          },
        ],
      },
      // push notifications
      {
        path: "notifications",
        children: [
          {
            path: "push",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PushNotification />
              </Suspense>
            ),
          },
          {
            path: "requests",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <NotificationRequests />
              </Suspense>
            ),
          },
        ],
      },
      // tags
      {
        path: "tags",
        element: (
          <Suspense fallback={<ThreeDotsLoader />}>
            <Tags />
          </Suspense>
        ),
      },
      // faq
      {
        path: "faq",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <Faq />
              </Suspense>
            ),
          },
          {
            path: "add",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <AddFaq />
              </Suspense>
            ),
          },
          {
            path: ":faq_id",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <EditFaq />
              </Suspense>
            ),
          },
        ],
      },
      // suggestions
      {
        path: "suggestions",
        element: (
          <Suspense fallback={<ThreeDotsLoader />}>
            <Suggestions />
          </Suspense>
        ),
      },
      // empty-search
      {
        path: "empty-search",
        element: (
          <Suspense fallback={<ThreeDotsLoader />}>
            <EmptySearchLog />
          </Suspense>
        ),
      },
      // banners
      {
        path: "/banners",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <Banners />
              </Suspense>
            ),
          },
          {
            path: "add",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <AddBanner />
              </Suspense>
            ),
          },
          {
            path: ":banner_id",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <EditBanner />
              </Suspense>
            ),
          },
        ],
      },
      // Settlements
      {
        path: "settlements",
        element: (
          <Suspense fallback={<ThreeDotsLoader />}>
            <Settlements />
          </Suspense>
        ),
      },
      // transactions
      {
        path: "transactions",
        element: (
          <Suspense fallback={<ThreeDotsLoader />}>
            <Transactions />
          </Suspense>
        ),
      },
      // log-permission
      {
        path: "log-permissions",
        element: (
          <Suspense fallback={<ThreeDotsLoader />}>
            <LogPermissions />
          </Suspense>
        ),
      },
      // logs
      {
        path: "logs",
        element: (
          <Suspense fallback={<ThreeDotsLoader />}>
            <Logs />
          </Suspense>
        ),
      },
      // settings
      {
        path: "settings",
        element: (
          <Suspense fallback={<ThreeDotsLoader />}>
            <Settings />
          </Suspense>
        ),
      },
      // service-packages
      {
        path: "service-packages",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <ServicePackages />
              </Suspense>
            ),
          },
          {
            path: "add",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <AddServicePackage />
              </Suspense>
            ),
          },
          {
            path: ":package_id",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <EditServicePackage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "service-purchase-requests",
        element: (
          <Suspense fallback={<ThreeDotsLoader />}>
            <ServicePurchaseRequests />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: "/marketer",
    element: (
      <ProtectedRoutes>
        <AllowedTo allowedTo={[ROLES_ID.MARKETER]}>
          <Suspense fallback={<p></p>}>
            <MainLayout />
          </Suspense>
        </AllowedTo>
      </ProtectedRoutes>
    ),
    children: [
      {
        index: true,
        element: <MarketerHome />,
      },
      {
        path: "business-forms",
        children: [
          {
            index: true,
            element: <MarketerBusinessForms />,
          },
          {
            path: "add",
            element: <AddBusinessForm />,
          },
          {
            path: ":form_id",
            element: <EditBusinessForm />,
          },
          {
            path: ":form_id/images",
            element: <BusinessFormImages />,
          },
        ],
      },
      // businessess/join-requests
      {
        path: "businesses/join-requests",
        children: [
          {
            index: true,
            element: (
              <UserHas userHas="can_manage_business_requests">
                <Suspense fallback={<ThreeDotsLoader />}>
                  <BusinessJoinRequests />
                </Suspense>
              </UserHas>
            ),
          },
        ],
      },
    ],
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/join",
    element: <JoinNewBusinessForm />,
  },
  {
    path: "/join/success",
    element: <JoinRequestSuccess />,
  },
]);
export default router;
